import { IconClose, IconPaperPlane } from "@aksara-ui/icons";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  InputGroup,
  InputText,
  Stack,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  theme,
} from "@aksara-ui/react";
import FilterKit from "components/FilterKit";
import { InputCheckbox } from "components/Form/InputCheckbox";
import { InputSearchbox } from "components/Form/InputSearchbox";
import {
  Pagination,
  PaginationDetail,
  PaginationFilter,
} from "components/Pagination";
import TableBodyRowsSkeleton from "components/TableBodyRowSkeleton";
import { FieldArray, Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import {
  getAllMembersModal,
  getAllRaffleCities,
  getAllRaffleWinners,
  setModalConfirmationOpen,
} from "redux/actions";
import { toCapitilizeCase } from "utilities/helper/toCapitalizeCase";
import CatalogGiftConfirmationModal from "./CatalogGiftConfirmationModal";
import { stringToColor } from "utilities/helper/stringToColor";

/**
 * Props interface for the CatalogGiftAddModal component
 * @interface Props
 * @property {string | undefined} catalogId - The ID of the catalog
 */
interface Props {
  catalogId?: string | undefined;
}

/**
 * Initial form values for the gift input
 */
const initialValues = {
  usersGiftInput: [
    {
      id: 0,
      ancol_id: "",
      name: "",
      email: "",
      phone_number: "",
      quantity: 1,
    },
  ],
};

/**
 * Interface representing a selected row in the member table
 * @interface SelectedRow
 * @property {string} id - Unique identifier for the row
 * @property {string} ancol_id - Ancol ID of the member
 * @property {string} name - Name of the member
 * @property {string} email - Email of the member
 * @property {string} phone_number - Phone number of the member
 * @property {number} quantity - Quantity of gifts to send
 */
interface SelectedRow {
  id: string;
  ancol_id: string;
  name: string;
  email: string;
  phone_number: string;
  quantity: number;
}

/**
 * Interface representing a selected row in the raffle winners table
 * @interface SelectedRowRaffle
 * @extends {SelectedRow}
 * @property {string} winner_id - Unique identifier for the raffle winner
 */
interface SelectedRowRaffle {
  winner_id: string;
  id: string;
  ancol_id: string;
  name: string;
  email: string;
  phone_number: string;
  quantity: number;
}

/**
 * Modal component for adding gift rewards to catalog members or raffle winners
 * @component
 * @param {Props & DialogProps} props - Component properties
 * @param {boolean} props.isOpen - Controls visibility of the modal
 * @param {function} props.onClose - Callback function when modal is closed
 * @param {string} props.catalogId - ID of the catalog being modified
 * @returns {React.FC} A modal component for gift reward distribution
 *
 * @example
 * <CatalogGiftAddModal 
 *   isOpen={true}
 *   onClose={() => {}}
 *   catalogId="123"
 * />
 */
const CatalogGiftAddModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  catalogId,
}) => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ maxWidth: 576 });
  const isTablet = useMediaQuery({ maxWidth: 1024 });

  const [selectedRows, setSelectedRows] = useState<SelectedRow[]>([]);
  const [selectedRowsRaffle, setSelectedRowsRaffle] = useState<
    SelectedRowRaffle[]
  >([]);
  const [availableStock, setAvailableStock] = useState(0);

  const { isLoadingModal, isModalConfirmationOpen } = useSelector(
    (state: any) => state.globalReducer
  );
  const { members, memberTierFilters, memberPortalFilters } = useSelector(
    (state: any) => state.memberReducer
  );
  const { catalogDetail } = useSelector((state: any) => state.catalogReducer);
  const {
    currentPage: memberCurrentPage,
    totalPage: memberTotalPage,
    limitPage: memberLimitPage,
    lengthPage: memberLengthPage,
    filterTenantPage,
    filterTierPage,
    filterPortalPage,
  } = useSelector((state: any) => state.memberReducer);
  const { raffleWinners } = useSelector(
    (state: any) => state.raffleWinnerReducer
  );
  const {
    currentPage: raffleCurrentPage,
    totalPage: raffleTotalPage,
    limitPage: raffleLimitPage,
    lengthPage: raffleLengthPage,
    // filterCityPage,
  } = useSelector((state: any) => state.raffleWinnerReducer);
  const { isFormLoading } = useSelector((state: any) => state.catalogReducer);

  const [searchValue, setSearchValue] = useState("");
  const [limit, setLimit] = useState(10);

  const handleSearchChange = (event: any) => {
    setSearchValue(event.target.value);
    dispatch(
      getAllMembersModal(
        1,
        event.target.value,
        memberLimitPage,
        filterTenantPage,
        filterTierPage,
        filterPortalPage
      ) as any
    );
  };

  const handleClearSearchChange = () => {
    setSearchValue("");
    dispatch(
      getAllMembersModal(
        memberCurrentPage,
        "",
        memberLimitPage,
        filterTenantPage,
        filterTierPage,
        filterPortalPage
      ) as any
    );
  };

  const handleCloseModal = () => {
    onClose && onClose();
    setSelectedRows([]);
    setSelectedRowsRaffle([]);
  };

  const handleSubmit = () => {
    dispatch(setModalConfirmationOpen(true) as any);
  };

  const handleCloseConfirmModal = () => {
    dispatch(setModalConfirmationOpen(false) as any);
  };

  useEffect(() => {
    if (isOpen) {
      if (catalogDetail.catalog_name === "Sheila On 7 Ticket") {
        // Push Reward Raffle
        dispatch(getAllRaffleCities() as any);
        dispatch(
          getAllRaffleWinners(
            raffleCurrentPage,
            "",
            raffleLimitPage,
            "4"
          ) as any
        );
      } else {
        // Push Reward
        dispatch(
          getAllMembersModal(
            memberCurrentPage,
            "",
            memberLimitPage,
            filterTenantPage,
            filterTierPage,
            filterPortalPage
          ) as any
        );
      }

      setAvailableStock(catalogDetail.available_stock);
    }
  }, [dispatch, isOpen, catalogDetail]);

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
      maxWidth={"90vw"}
      maxHeight={"90vh"}
      style={{
        height: "80vh",
        display: "flex",
        alignContent: "center",
      }}
    >
      <Formik initialValues={initialValues} onSubmit={handleSubmit}>
        {({ values, setFieldValue }) => {
          return (
            <Form>
              <DialogHeader>Send Gift Reward</DialogHeader>
              <DialogContent>
                <Box
                  display={"flex"}
                  flexDirection={isTablet ? "column" : "row"}
                  style={{ gap: 5 }}
                  marginBottom={10}
                >
                  <InputSearchbox
                    autoComplete="off"
                    name="textDummy"
                    placeholder="Search..."
                    size={32}
                    onChange={handleSearchChange}
                    onClearButton={handleClearSearchChange}
                  />

                  <Box
                    display={"flex"}
                    flexDirection={isMobile ? "column" : "row"}
                    justifyContent={"left"}
                    style={{ gap: 5 }}
                  >
                    {catalogDetail.catalog_name === "Sheila On 7 Ticket" ? (
                      <></>
                    ) : (
                      <>
                        <FilterKit
                          orderByOptions={[
                            {
                              key: memberTierFilters,
                              label: "Tier",
                              options: memberTierFilters.map((tier: any) => {
                                return {
                                  label: tier,
                                  value: tier !== "All" ? tier : "",
                                };
                              }),
                            },
                          ]}
                          onChangeOrder={(e: any) => {
                            dispatch(
                              getAllMembersModal(
                                1,
                                "",
                                memberLimitPage,
                                filterTenantPage,
                                e[memberTierFilters]
                                  ? e[memberTierFilters].value
                                  : "",
                                filterPortalPage
                              ) as any
                            );
                          }}
                        />

                        <FilterKit
                          orderByOptions={[
                            {
                              key: memberPortalFilters,
                              label: "Portal",
                              options: memberPortalFilters.map(
                                (portal: any) => {
                                  return {
                                    label: portal,
                                    value: portal !== "All" ? portal : "",
                                  };
                                }
                              ),
                            },
                          ]}
                          onChangeOrder={(e: any) => {
                            dispatch(
                              getAllMembersModal(
                                1,
                                "",
                                memberLimitPage,
                                filterTenantPage,
                                filterTierPage,
                                e[memberPortalFilters]
                                  ? e[memberPortalFilters].value
                                  : ""
                              ) as any
                            );
                          }}
                        />
                      </>
                    )}
                  </Box>
                </Box>
                <Card maxHeight={"100%"} overflow={"auto"} elevation={2}>
                  <FieldArray name="usersGiftInput">
                    {() => {
                      return (
                        values.usersGiftInput.length > 0 &&
                        values.usersGiftInput.map((userGift, index) => {
                          return (
                            <Table key={index}>
                              <TableHead
                                position={"sticky"}
                                top={0}
                                backgroundColor={"white"}
                              >
                                <TableHeadRow>
                                  <TableHeadCell textAlign={"center"}>
                                    <Box
                                      display={"flex"}
                                      justifyContent={"center"}
                                    >
                                      <InputCheckbox
                                        checked={
                                          catalogDetail.catalog_name ===
                                          "Sheila On 7 Ticket"
                                            ? selectedRowsRaffle.length > 0 &&
                                              selectedRowsRaffle.length <
                                                raffleWinners.length
                                              ? "indeterminate"
                                              : !!selectedRowsRaffle.length
                                            : selectedRows.length > 0 &&
                                              selectedRows.length <
                                                members.length
                                            ? "indeterminate"
                                            : !!selectedRows.length
                                        }
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            if (
                                              catalogDetail.catalog_name ===
                                              "Sheila On 7 Ticket"
                                            ) {
                                              setSelectedRowsRaffle(
                                                raffleWinners.map((winner: any) => ({
                                                  winner_id: winner.id,
                                                  id: winner.raffle.user.id,
                                                  ancol_id: winner.raffle.user.ancol_id,
                                                  name: winner.raffle.user.name,
                                                  email: winner.raffle.user.email,
                                                  phone_number:
                                                    winner.raffle.user.phone_number,
                                                  quantity: 1,
                                                }))
                                              );
                                            } else {
                                              setSelectedRows(
                                                members.map(
                                                  ({
                                                    id,
                                                    ancol_id,
                                                    name,
                                                    email,
                                                    phone_number,
                                                  }: any) => ({
                                                    id: id,
                                                    ancol_id: ancol_id,
                                                    name: name,
                                                    email: email,
                                                    phone_number: phone_number,
                                                    quantity: 1,
                                                  })
                                                )
                                              );
                                            }
                                            if (
                                              catalogDetail.catalog_name ===
                                              "Sheila On 7 Ticket"
                                            ) {
                                              setAvailableStock(
                                                catalogDetail.available_stock -
                                                  raffleWinners.length
                                              );
                                            } else {
                                              setAvailableStock(
                                                catalogDetail.available_stock -
                                                  members.length
                                              );
                                            }
                                          } else {
                                            setSelectedRows([]);
                                            setSelectedRowsRaffle([]);
                                            setAvailableStock(
                                              catalogDetail.available_stock
                                            );
                                          }
                                        }}
                                      />
                                    </Box>
                                  </TableHeadCell>
                                  {catalogDetail.catalog_name ===
                                  "Sheila On 7 Ticket" ? (
                                    <>
                                      <TableHeadCell>Raffle Code</TableHeadCell>
                                      <TableHeadCell>Ancol ID</TableHeadCell>
                                      <TableHeadCell>Name</TableHeadCell>
                                      <TableHeadCell>Email</TableHeadCell>
                                      <TableHeadCell>
                                        Phone Number
                                      </TableHeadCell>
                                    </>
                                  ) : (
                                    <>
                                      <TableHeadCell>Ancol ID</TableHeadCell>
                                      <TableHeadCell>Name</TableHeadCell>
                                      <TableHeadCell>Email</TableHeadCell>
                                      <TableHeadCell>
                                        Phone Number
                                      </TableHeadCell>
                                      <TableHeadCell>Tier</TableHeadCell>
                                      <TableHeadCell>Portal</TableHeadCell>
                                    </>
                                  )}
                                  <TableHeadCell>Quantity</TableHeadCell>
                                </TableHeadRow>
                              </TableHead>
                              {catalogDetail.catalog_name ===
                              "Sheila On 7 Ticket" ? (
                                <TableBody>
                                  {isLoadingModal ? (
                                    <TableBodyRowsSkeleton
                                      rowsNumber={10}
                                      columnsNumber={7}
                                    />
                                  ) : !raffleWinners.length ? (
                                    <TableBodyRow>
                                      <TableBodyCell
                                        colSpan={7}
                                        textAlign={"center"}
                                      >
                                        No reward available
                                      </TableBodyCell>
                                    </TableBodyRow>
                                  ) : (
                                    raffleWinners.map((member: any) => {
                                      const selectedRowIndex =
                                        selectedRowsRaffle.findIndex(
                                          (selectedRow) =>
                                            selectedRow.winner_id === member.id
                                        );

                                      const name = member.raffle.user.name;
                                      const color = stringToColor(name);

                                      const isChecked = selectedRowIndex !== -1;
                                      return (
                                        <TableBodyRow
                                          key={member.id}
                                          backgroundColor={
                                            isChecked
                                              ? theme.colors.blue01
                                              : theme.colors.white
                                          }
                                        >
                                          <TableBodyCell textAlign={"center"}>
                                            <Box
                                              display={"flex"}
                                              justifyContent={"center"}
                                            >
                                              <InputCheckbox
                                                checked={isChecked}
                                                onCheckedChange={(checked) => {
                                                  if (checked) {
                                                    setSelectedRowsRaffle(
                                                      (prev) => [
                                                        ...prev,
                                                        {
                                                          winner_id: member.id,
                                                          ...member.raffle.user,
                                                          quantity: 1,
                                                        },
                                                      ]
                                                    );
                                                    setAvailableStock(
                                                      availableStock - 1
                                                    );
                                                  } else {
                                                    setSelectedRowsRaffle(
                                                      (prev) =>
                                                        prev.filter(
                                                          (item) =>
                                                            item[
                                                              "winner_id"
                                                            ] !== member.id
                                                        )
                                                    );
                                                    setAvailableStock(
                                                      availableStock +
                                                        selectedRowsRaffle[
                                                          selectedRowIndex
                                                        ].quantity
                                                    );
                                                  }
                                                }}
                                              />
                                            </Box>
                                          </TableBodyCell>
                                          <TableBodyCell whiteSpace={"nowrap"}>
                                            <Badge
                                              size="lg"
                                              variant="default"
                                              style={{
                                                paddingLeft: 20,
                                                paddingRight: 20,
                                                fontSize: 12,
                                                fontWeight: 600,
                                              }}
                                            >
                                              {member.giveaway_code.toLocaleString()}
                                            </Badge>
                                          </TableBodyCell>
                                          <TableBodyCell>
                                            {member.raffle.user.ancol_id}
                                          </TableBodyCell>
                                          <TableBodyCell whiteSpace={"nowrap"}>
                                            <Avatar
                                              name={name}
                                              backgroundColor={color}
                                              style={{ marginRight: 15 }}
                                            />
                                            {name}
                                          </TableBodyCell>
                                          <TableBodyCell>
                                            {member.raffle.user.email}
                                          </TableBodyCell>
                                          <TableBodyCell whiteSpace={"nowrap"}>
                                            +62{" "}
                                            {member.raffle.user.phone_number.slice(
                                              2
                                            )}
                                          </TableBodyCell>
                                          <TableBodyCell>
                                            <Stack spacing="xs">
                                              <InputGroup inputSize={40}>
                                                <InputText
                                                  type="number"
                                                  size={40}
                                                  width={80}
                                                  value={
                                                    isChecked
                                                      ? selectedRowsRaffle[
                                                          selectedRowIndex
                                                        ].quantity
                                                      : 0
                                                  }
                                                  onChange={(e) => {
                                                    const newValue = parseInt(
                                                      e.target.value,
                                                      10
                                                    );
                                                    const updatedRows = [
                                                      ...selectedRowsRaffle,
                                                    ];
                                                    const difference =
                                                      newValue -
                                                      selectedRowsRaffle[
                                                        selectedRowIndex
                                                      ].quantity;
                                                    updatedRows[
                                                      selectedRowIndex
                                                    ] = {
                                                      ...updatedRows[
                                                        selectedRowIndex
                                                      ],
                                                      quantity: newValue,
                                                    };
                                                    setSelectedRowsRaffle(
                                                      updatedRows
                                                    );
                                                    setAvailableStock(
                                                      availableStock -
                                                        difference
                                                    );
                                                  }}
                                                />
                                              </InputGroup>
                                            </Stack>
                                          </TableBodyCell>
                                        </TableBodyRow>
                                      );
                                    })
                                  )}
                                </TableBody>
                              ) : (
                                <TableBody>
                                  {isLoadingModal ? (
                                    <TableBodyRowsSkeleton
                                      rowsNumber={10}
                                      columnsNumber={8}
                                    />
                                  ) : !members.length ? (
                                    <TableBodyRow>
                                      <TableBodyCell
                                        colSpan={8}
                                        textAlign={"center"}
                                      >
                                        No reward available
                                      </TableBodyCell>
                                    </TableBodyRow>
                                  ) : (
                                    members.map((member: any) => {
                                      const selectedRowIndex =
                                        selectedRows.findIndex(
                                          (selectedRow) =>
                                            selectedRow["id"] === member.id
                                        );
                                      const name = member.name;
                                      const color = stringToColor(name);

                                      const isChecked = selectedRowIndex !== -1;
                                      return (
                                        <TableBodyRow
                                          key={member.id}
                                          backgroundColor={
                                            isChecked
                                              ? theme.colors.blue01
                                              : theme.colors.white
                                          }
                                        >
                                          <TableBodyCell textAlign={"center"}>
                                            <Box
                                              display={"flex"}
                                              justifyContent={"center"}
                                            >
                                              <InputCheckbox
                                                checked={isChecked}
                                                onCheckedChange={(checked) => {
                                                  if (checked) {
                                                    setSelectedRows((prev) => [
                                                      ...prev,
                                                      {
                                                        ...member,
                                                        quantity: 1,
                                                      },
                                                    ]);
                                                    setAvailableStock(
                                                      availableStock - 1
                                                    );
                                                  } else {
                                                    setSelectedRows((prev) =>
                                                      prev.filter(
                                                        (item) =>
                                                          item["id"] !==
                                                          member.id
                                                      )
                                                    );
                                                    setAvailableStock(
                                                      availableStock +
                                                        selectedRows[
                                                          selectedRowIndex
                                                        ].quantity
                                                    );
                                                  }
                                                }}
                                              />
                                            </Box>
                                          </TableBodyCell>
                                          <TableBodyCell>
                                            {member.ancol_id}
                                          </TableBodyCell>
                                          <TableBodyCell whiteSpace={"nowrap"}>
                                            <Avatar
                                              name={name}
                                              backgroundColor={color}
                                              style={{ marginRight: 15 }}
                                            />
                                            {name}
                                          </TableBodyCell>
                                          <TableBodyCell>
                                            {member.email}
                                          </TableBodyCell>
                                          <TableBodyCell whiteSpace={"nowrap"}>
                                            +62 {member.phone_number.slice(2)}
                                          </TableBodyCell>
                                          <TableBodyCell whiteSpace={"nowrap"}>
                                            <Badge
                                              size="md"
                                              style={{
                                                backgroundImage: `${member.tier_color}`,
                                                color: `${
                                                  member.tier_name !== "silver"
                                                    ? "#FAFBFD"
                                                    : "#949DA7"
                                                }`,
                                              }}
                                            >
                                              {toCapitilizeCase(
                                                member.tier_name
                                              )}
                                            </Badge>
                                          </TableBodyCell>
                                          <TableBodyCell>
                                            <Badge
                                              size={"md"}
                                              variant={"warning"}
                                            >
                                              {member.portal_name}
                                            </Badge>
                                          </TableBodyCell>
                                          <TableBodyCell>
                                            <Stack spacing="xs">
                                              <InputGroup inputSize={40}>
                                                <InputText
                                                  type="number"
                                                  size={40}
                                                  width={80}
                                                  value={
                                                    isChecked
                                                      ? selectedRows[
                                                          selectedRowIndex
                                                        ].quantity
                                                      : 0
                                                  }
                                                  onChange={(e) => {
                                                    const newValue = parseInt(
                                                      e.target.value,
                                                      10
                                                    );
                                                    const updatedRows = [
                                                      ...selectedRows,
                                                    ];
                                                    const difference =
                                                      newValue -
                                                      selectedRows[
                                                        selectedRowIndex
                                                      ].quantity;
                                                    updatedRows[
                                                      selectedRowIndex
                                                    ] = {
                                                      ...updatedRows[
                                                        selectedRowIndex
                                                      ],
                                                      quantity: newValue,
                                                    };
                                                    setSelectedRows(
                                                      updatedRows
                                                    );
                                                    setAvailableStock(
                                                      availableStock -
                                                        difference
                                                    );
                                                  }}
                                                />
                                              </InputGroup>
                                            </Stack>
                                          </TableBodyCell>
                                        </TableBodyRow>
                                      );
                                    })
                                  )}
                                </TableBody>
                              )}
                            </Table>
                          );
                        })
                      );
                    }}
                  </FieldArray>
                </Card>

                {catalogDetail.catalog_name === "Sheila On 7 Ticket" ? (
                  <Box
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    paddingY={4}
                  >
                    {!isMobile ? (
                      <PaginationDetail
                        page={raffleCurrentPage}
                        limit={raffleLimitPage}
                        length={raffleLengthPage}
                      />
                    ) : (
                      ""
                    )}

                    <Pagination
                      current={raffleCurrentPage}
                      total={raffleTotalPage}
                      onSelect={(select: number) =>
                        dispatch(
                          getAllRaffleWinners(
                            select,
                            searchValue,
                            raffleLimitPage,
                            "4"
                          ) as any
                        )
                      }
                    />
                    <PaginationFilter
                      selectedItem={limit}
                      onChange={(selectedItem) => {
                        if (selectedItem) {
                          setLimit(selectedItem);
                          dispatch(
                            getAllRaffleWinners(
                              1,
                              searchValue,
                              selectedItem,
                              "4"
                            ) as any
                          );
                        }
                      }}
                      items={[10, 20, 30, 40, 50]}
                    />
                  </Box>
                ) : (
                  <Box
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    paddingY={4}
                  >
                    {!isMobile ? (
                      <PaginationDetail
                        page={memberCurrentPage}
                        limit={memberLimitPage}
                        length={memberLengthPage}
                      />
                    ) : (
                      ""
                    )}

                    <Pagination
                      current={memberCurrentPage}
                      total={memberTotalPage}
                      onSelect={(select: number) =>
                        dispatch(
                          getAllMembersModal(
                            select,
                            searchValue,
                            memberLimitPage,
                            filterTenantPage,
                            filterTierPage,
                            filterPortalPage
                          ) as any
                        )
                      }
                    />
                    <PaginationFilter
                      selectedItem={limit}
                      onChange={(selectedItem) => {
                        if (selectedItem) {
                          setLimit(selectedItem);
                          dispatch(
                            getAllMembersModal(
                              1,
                              searchValue,
                              selectedItem,
                              filterTenantPage,
                              filterTierPage,
                              filterPortalPage
                            ) as any
                          );
                        }
                      }}
                      items={[10, 20, 30, 40, 50]}
                    />
                  </Box>
                )}

                <CatalogGiftConfirmationModal
                  catalogId={catalogId}
                  users={
                    catalogDetail.catalog_name === "Sheila On 7 Ticket"
                      ? selectedRowsRaffle
                      : selectedRows
                  }
                  isOpen={isModalConfirmationOpen}
                  onClose={() => handleCloseConfirmModal()}
                />
              </DialogContent>
              <DialogFooter
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 20,
                }}
              >
                <Stack
                  display={"flex"}
                  flexDirection={"row"}
                  justifyContent={"left"}
                  style={{ gap: 5 }}
                >
                  <Text scale={400} fontWeight={500}>
                    Available Stock :
                  </Text>
                  <Text
                    scale={400}
                    fontWeight={700}
                    color={
                      availableStock < 1
                        ? theme.colors.red06
                        : theme.colors.green07
                    }
                  >
                    {availableStock}
                  </Text>
                </Stack>

                <Stack
                  display={"flex"}
                  flexDirection={isMobile ? "column" : "row"}
                  justifyContent={"right"}
                  spacing={"xs"}
                  flex={1}
                  style={{ gap: isMobile ? 5 : 15 }}
                  paddingY={0}
                >
                  <Button
                    variant={"outline"}
                    onClick={() => handleCloseModal()}
                    icon={IconClose}
                    iconPosition={"left"}
                    style={{
                      color: "white",
                      backgroundColor: `${theme.colors.red05}`,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type={"submit"}
                    variant={"primary"}
                    isLoading={isFormLoading}
                    icon={IconPaperPlane}
                    iconPosition={"left"}
                  >
                    Send Gift Reward
                  </Button>
                </Stack>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default CatalogGiftAddModal;
